import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Clock, Copy, AlertCircle, Truck, FileText, MessageCircle, Send, Users, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import BillOfLading from "@/components/BillOfLading";

import getApiUrl from "@/utils/api";
import { getConfirmation } from "@/utils/confirmation";

const API = getApiUrl();

export default function BookingSuccess() {
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState("checking");
  const [showBillOfLading, setShowBillOfLading] = useState(false);
  const [myReferralCode, setMyReferralCode] = useState("");

  const sessionId = searchParams.get("session_id");
  const bookingId = searchParams.get("booking_id");
  const paymentType = searchParams.get("payment");

  useEffect(() => {
    if (sessionId) {
      pollStripeStatus();
    } else if (bookingId) {
      fetchBooking(bookingId);
      if (paymentType === "manual") {
        const storedPaymentInfo = localStorage.getItem("paymentInfo");
        if (storedPaymentInfo) {
          setPaymentInfo(JSON.parse(storedPaymentInfo));
        }
      }
    }
  }, [sessionId, bookingId, paymentType]);

  const pollStripeStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus("timeout");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setPaymentStatus("paid");
        // Get booking details
        const bookingRes = await axios.get(`${API}/bookings/check/${sessionId}`);
        if (bookingRes.data.length > 0) {
          setBooking(bookingRes.data[0]);
        }
        setLoading(false);
      } else if (response.data.status === "expired") {
        setPaymentStatus("expired");
        setLoading(false);
      } else {
        setPaymentStatus("processing");
        setTimeout(() => pollStripeStatus(attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      // Still try to get the booking
      setPaymentStatus("paid");
      setLoading(false);
    }
  };

  const fetchBooking = async (id) => {
    try {
      const response = await axios.get(`${API}/bookings/${id}`);
      setBooking(response.data);
      setPaymentStatus(response.data.payment_status);
      // Auto-generate a referral code for this passenger so the Share Trip
      // WhatsApp button includes it. Idempotent: backend returns their existing
      // code if one already exists for their WhatsApp number.
      if (response.data?.whatsapp_number && ["arrival", "return"].includes(response.data.trip_type)) {
        try {
          const refRes = await axios.post(`${API}/referrals/create`, {
            name: response.data.full_name,
            whatsapp_number: response.data.whatsapp_number,
            email: response.data.email || "",
          });
          const code = refRes.data?.referral?.code || refRes.data?.referral?.referral_code || "";
          if (code) setMyReferralCode(code);
        } catch (e) { /* referral generation is best-effort */ }
      }
    } catch (error) {
      console.error("Failed to fetch booking:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4" data-testid="booking-success-page">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-2xl border-0 rounded-3xl overflow-hidden">
          <div className={`p-8 text-center ${paymentStatus === "paid" ? "bg-green-500" : "bg-yellow-500"}`}>
            {paymentStatus === "paid" ? (
              <CheckCircle className="w-20 h-20 text-white mx-auto mb-4" />
            ) : (
              <Clock className="w-20 h-20 text-white mx-auto mb-4" />
            )}
            <h1 className="text-3xl font-extrabold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {paymentStatus === "paid" ? "Booking Confirmed!" : "Booking Created!"}
            </h1>
            <p className="text-white/90">
              {paymentStatus === "paid" 
                ? "Your payment has been received" 
                : "Complete your payment to confirm"}
            </p>
          </div>

          <CardContent className="p-8">
            {booking && (
              <div className="space-y-6">
                <div className="ticket-stub bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-6 border border-dashed border-primary/30">
                  {booking.confirmation_number && (
                    <div className="flex items-center justify-between mb-4 pb-4 border-b border-dashed border-primary/20">
                      <div>
                        <span className="text-xs text-muted-foreground uppercase tracking-wide">Confirmation Number</span>
                        <p className="text-2xl font-extrabold font-mono text-primary">{booking.confirmation_number}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => copyToClipboard(booking.confirmation_number)} data-testid="copy-confirmation-btn">
                        <Copy className="w-4 h-4 mr-1" /> Copy
                      </Button>
                    </div>
                  )}
                  <h3 className="font-bold text-lg mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Trip Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Booking ID</span>
                      <p className="font-mono font-semibold text-xs break-all">{booking.id}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Trip Type</span>
                      <p className="font-semibold capitalize">{booking.trip_type}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Date</span>
                      <p className="font-semibold">{booking.travel_date}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Time</span>
                      <p className="font-semibold">{booking.travel_time}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">From</span>
                      <p className="font-semibold">{booking.pickup_location}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">To</span>
                      <p className="font-semibold">{booking.dropoff_location}</p>
                    </div>
                  </div>
                </div>

                {/* Manual Payment Instructions */}
                {paymentType === "manual" && paymentInfo && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-bold text-yellow-800 mb-2">Complete Your Payment</h4>
                        <p className="text-yellow-700 mb-4">{paymentInfo.payment_info.instructions}</p>
                        
                        <div className="bg-white rounded-lg p-4 border border-yellow-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-muted-foreground">Send to:</p>
                              <p className="font-bold text-lg">
                                {paymentInfo.payment_info.handle || paymentInfo.payment_info.email}
                              </p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyToClipboard(paymentInfo.payment_info.handle || paymentInfo.payment_info.email)}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-sm text-muted-foreground">Amount:</p>
                            <p className="font-bold text-2xl text-primary">${paymentInfo.amount.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Status Badge */}
                <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                  <div>
                    <p className="text-sm text-muted-foreground">Payment Status</p>
                    <p className={`font-bold ${paymentStatus === "paid" ? "text-green-600" : "text-yellow-600"}`}>
                      {paymentStatus === "paid" ? "Paid" : "Pending Verification"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Booking Status</p>
                    <p className="font-bold text-primary">
                      {booking.booking_status === "confirmed" ? "Confirmed" : "Awaiting Confirmation"}
                    </p>
                  </div>
                </div>

                {/* T&M Courier Shipping Section — show for return trips with barrels/shipping items */}
                {(booking.uses_shipping_company || (booking.trip_type === "return" && (booking.total_barrels || 0) > 0)) && (
                  <div className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border border-orange-200">
                    <div className="flex items-start gap-3">
                      <Truck className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <h4 className="font-bold text-orange-800 mb-2">T&M Courier Services — Bill of Lading</h4>
                        <p className="text-orange-700 text-sm mb-3">
                          {(booking.total_barrels || 0) > 0
                            ? `You have ${booking.total_barrels} barrel${booking.total_barrels > 1 ? "s" : ""} to ship. Fill out the Bill of Lading so T&M Courier can arrange pickup and delivery. Include the receiver's name, address, and 2 phone numbers for customs.`
                            : "You selected shipping with T&M Courier. Fill out the Bill of Lading form for your shipment."
                          }
                        </p>
                        <Button 
                          onClick={() => setShowBillOfLading(!showBillOfLading)}
                          className="bg-orange-500 hover:bg-orange-600 text-white"
                          data-testid="toggle-bol-btn"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          {showBillOfLading ? "Hide Bill of Lading" : "Fill Out Bill of Lading"}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="text-center text-sm text-muted-foreground">
                  <p>Our operations team will contact you via WhatsApp to confirm your trip details.</p>
                </div>

                {/* Share Trip with a Friend — Growth loop for shared-ride discount */}
                {["arrival", "return"].includes(booking.trip_type) && (() => {
                  const isReturn = booking.trip_type === "return";
                  const airportLc = String(isReturn ? booking.dropoff_location : booking.pickup_location || "").toLowerCase();
                  const isMadison = /madison|msn|dane county|dane regional/.test(airportLc);
                  // Madison returns are already flat-priced ($65) — no group-discount hook to sell.
                  if (isReturn && isMadison) return null;
                  const airport = /midway|mdw/.test(airportLc) ? "MDW"
                    : /milwaukee|mke/.test(airportLc) ? "MKE"
                    : /madison|msn|dane/.test(airportLc) ? "MSN"
                    : "ORD";
                  const soloPrice = isReturn ? 200 : 250;
                  const sharedPrice = isReturn ? 85 : 120;
                  const link = `${window.location.origin}/book?date=${encodeURIComponent(booking.travel_date)}${myReferralCode ? `&ref=${encodeURIComponent(myReferralCode)}` : ""}`;
                  const refLine = myReferralCode
                    ? `\n\nBonus: use my code *${myReferralCode}* for an extra $5 off. We both get credit! 🎁`
                    : "";
                  const msg = encodeURIComponent(
                    `Hey! I just booked an Easy Transportation ${isReturn ? "return trip" : "airport pickup"} for ${booking.travel_date} at ${booking.travel_time} to ${airport}.\n\n` +
                    `If you book the same day + same airport + same time window, we BOTH drop from $${soloPrice} → $${sharedPrice} (shared ride).${refLine}\n\n` +
                    `Book here: ${link}`
                  );
                  return (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5" data-testid="share-trip-friend-section">
                      <div className="flex items-start gap-3">
                        <Users className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="font-bold text-emerald-800 mb-1">
                            {isReturn ? "Traveling with a friend? Split the ride." : "Share and save together"}
                          </h4>
                          <p className="text-emerald-700 text-sm mb-3">
                            Your {isReturn ? "return" : "airport"} trip is currently at the solo rate. If another passenger books the same day + airport + time-window, you both auto-drop to <span className="font-bold">${sharedPrice}</span> (from ${soloPrice}). Send this to a friend or classmate:
                          </p>
                          {myReferralCode && (
                            <div className="bg-white border border-emerald-300 rounded-lg p-3 mb-3" data-testid="my-referral-code-card">
                              <p className="text-xs text-emerald-700 mb-1">Your personal referral code (auto-included in the message):</p>
                              <div className="flex items-center justify-between gap-2">
                                <code className="text-lg font-bold text-emerald-800 font-mono tracking-wide">{myReferralCode}</code>
                                <Button size="sm" variant="outline" className="h-7 border-emerald-300 text-emerald-700" onClick={() => copyToClipboard(myReferralCode)} data-testid="copy-referral-code">
                                  <Copy className="w-3 h-3 mr-1" />Copy
                                </Button>
                              </div>
                              <p className="text-[11px] text-emerald-600 mt-1">Every friend who books with this code earns you both $5 credit.</p>
                            </div>
                          )}
                          <a href={`https://wa.me/?text=${msg}`} target="_blank" rel="noopener noreferrer">
                            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-10 px-5" data-testid="share-trip-friend-btn">
                              <MessageCircle className="w-4 h-4 mr-2" />
                              Share Trip via WhatsApp
                            </Button>
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Share Confirmation with Other Passengers */}
                {booking.passengers && booking.passengers.length > 1 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5" data-testid="share-confirmation-section">
                    <div className="flex items-start gap-3">
                      <Share2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-bold text-blue-800 mb-1">Share Confirmation with Passengers</h4>
                        <p className="text-blue-700 text-sm mb-3">
                          Send the confirmation number to other passengers in your group so they have it for reference.
                        </p>
                        <div className="space-y-2">
                          {booking.passengers.filter((_, i) => i > 0).map((pax, idx) => {
                            const paxName = pax.name || pax.full_name || `Passenger ${idx + 2}`;
                            const paxWhatsapp = pax.whatsapp || pax.whatsapp_number || "";
                            const shareMsg = encodeURIComponent(
                              `Hi ${paxName}! Here is your Easy Transportation booking confirmation:\n\n` +
                              `Confirmation #: ${getConfirmation(booking)}\n` +
                              `Date: ${booking.travel_date}\n` +
                              `Time: ${booking.travel_time}\n` +
                              `From: ${booking.pickup_location}\n` +
                              `To: ${booking.dropoff_location}\n\n` +
                              `Check your booking status at: ${window.location.origin}/check-status`
                            );
                            const cleanNum = paxWhatsapp.replace(/[^0-9+]/g, "");
                            const whatsappUrl = cleanNum
                              ? `https://wa.me/${cleanNum.startsWith('+') ? cleanNum.slice(1) : cleanNum}?text=${shareMsg}`
                              : `https://wa.me/?text=${shareMsg}`;

                            return (
                              <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-100">
                                <div className="flex items-center gap-2">
                                  <Users className="w-4 h-4 text-blue-500" />
                                  <span className="text-sm font-medium">{paxName}</span>
                                  {paxWhatsapp && (
                                    <span className="text-xs text-muted-foreground">{paxWhatsapp}</span>
                                  )}
                                </div>
                                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white h-8 px-3" data-testid={`share-pax-${idx}`}>
                                    <Send className="w-3.5 h-3.5 mr-1" />
                                    Send
                                  </Button>
                                </a>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Share via WhatsApp (for any booking with multiple passengers but no individual passenger list) */}
                {booking.num_passengers > 1 && (!booking.passengers || booking.passengers.length <= 1) && (
                  <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5" data-testid="share-generic-section">
                    <div className="flex items-start gap-3">
                      <Share2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-bold text-blue-800 mb-1">Share with Your Travel Group</h4>
                        <p className="text-blue-700 text-sm mb-3">
                          Send the confirmation details to the other {booking.num_passengers - 1} passenger{booking.num_passengers > 2 ? 's' : ''} in your booking.
                        </p>
                        <a
                          href={`https://wa.me/?text=${encodeURIComponent(
                            `Here is your Easy Transportation booking confirmation:\n\n` +
                            `Confirmation #: ${getConfirmation(booking)}\n` +
                            `Date: ${booking.travel_date}\n` +
                            `Time: ${booking.travel_time}\n` +
                            `From: ${booking.pickup_location}\n` +
                            `To: ${booking.dropoff_location}\n\n` +
                            `Check your booking status at: ${window.location.origin}/check-status`
                          )}`}
                          target="_blank" rel="noopener noreferrer"
                        >
                          <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full h-9 px-5" data-testid="share-generic-btn">
                            <Share2 className="w-4 h-4 mr-2" />
                            Share Confirmation via WhatsApp
                          </Button>
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {/* WhatsApp Opt-in */}
                <div className="bg-green-50 border border-green-200 rounded-2xl p-5" data-testid="whatsapp-optin-section">
                  <div className="flex items-start gap-3">
                    <MessageCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-bold text-green-800 mb-1">Get WhatsApp Confirmations</h4>
                      <p className="text-green-700 text-sm mb-2">
                        Receive trip updates, driver info, and booking confirmations directly on WhatsApp.
                      </p>
                      <div className="bg-white rounded-lg p-3 border border-green-200 mb-3">
                        <p className="text-sm text-gray-700 font-medium mb-1">How to opt-in:</p>
                        <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
                          <li>Tap the button below — it will open WhatsApp</li>
                          <li>A message saying <span className="font-mono font-bold bg-green-100 px-1 rounded">Join on-take</span> will be pre-filled</li>
                          <li>Do not edit the message — just press <span className="font-bold">Send</span>!</li>
                        </ol>
                      </div>
                      <a
                        href="https://wa.me/14155238886?text=Join%20on-take"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="whatsapp-optin-link"
                      >
                        <Button className="bg-green-600 hover:bg-green-700 text-white rounded-full h-10 px-6">
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Opt-in to WhatsApp Updates
                        </Button>
                      </a>
                      <p className="text-xs text-green-600 mt-2">
                        Opens WhatsApp with a pre-filled "Join on-take" message — do not edit, just press Send!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Bill of Lading Form */}
            {showBillOfLading && booking && (
              <div className="mt-8">
                <BillOfLading bookingData={booking} />
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Link to="/" className="flex-1">
                <Button variant="outline" className="w-full rounded-full h-12">
                  Back to Home
                </Button>
              </Link>
              <Link to="/check-status" className="flex-1">
                <Button className="w-full rounded-full h-12 bg-primary text-primary-foreground">
                  Check Booking Status
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
